import React, { useEffect, useRef, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Reveals children once they scroll into view.
 *
 * variant: "up" (default) | "mask" | "scale"
 * delay:   seconds, staggered via the --reveal-delay custom property
 * as:      element tag to render (defaults to div)
 */
const Reveal = ({
  children,
  variant = "up",
  delay = 0,
  as: Tag = "div",
  className = "",
  threshold = 0.15,
  ...rest
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  const variantClass = variant === "up" ? "" : ` reveal--${variant}`;
  const style = delay ? { "--reveal-delay": `${delay}s` } : undefined;
  const classes = `reveal${variantClass}${shown ? " is-in" : ""} ${className}`.trim();

  /**
   * The mask variant clips an inner element rather than the observed one:
   * Chrome reports an intersectionRatio of 0 for a target whose own clip-path
   * hides it, which would leave the reveal permanently stuck closed.
   */
  if (variant === "mask") {
    return (
      <Tag ref={ref} className={classes} style={style} {...rest}>
        <span className="reveal__inner">{children}</span>
      </Tag>
    );
  }

  return (
    <Tag ref={ref} className={classes} style={style} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
